import cns from 'classnames'
import { BigNumberish } from '@ethersproject/bignumber'

import { useLoanCalcs } from 'modules/pools/hooks/useLoanCalcs'
import { useLoanCollateralCalcs } from 'modules/pools/hooks/useLoanCollateralCalcs'

import { Text } from 'shared/ui/common/Text'
import { Button } from 'shared/ui/controls/Button'
import { InfoFieldValue } from 'shared/ui/common/InfoFieldValue'
import { InfoFieldValueCouple } from 'shared/ui/common/InfoFieldValueCouple'
import { DashboardRow } from 'shared/ui/common/DashboardRow'
import { FormattedDate } from 'shared/ui/utils/FormattedDate'
import { LoanStatusBadge } from '../LoanStatusBadge'

import { formatAsset } from 'modules/pools/utils/formatAsset'
import { getPoolAssetIcon } from 'modules/pools/constants/poolAssets'
import { trimMiddleString } from 'shared/utils/trimMiddleString'
import type { Loan } from 'modules/pools/types/Loan'
import * as links from 'modules/router/links'
import { isLoanActive } from 'modules/pools/utils/isLoanActive'

import s from './DashboardRowLoan.module.scss'

type Props = {
  loan: Loan
  loanId: string
  isCompleted: boolean
  totalOutstanding?: number
  collateralAddress?: string
  totalCollateralAmount?: BigNumberish
  className?: string
}

export function DashboardRowLoan({
  loan,
  loanId,
  isCompleted,
  totalOutstanding,
  collateralAddress,
  totalCollateralAmount,
  className,
}: Props) {
  const {
    interestRate,
    borrowedAsset,
    principal,
    interest,
    totalDebt,
    totalDebtUSD,
    maturityDate,
    daysLeftToMaturity,
    issueDate,
    ltv,
  } = useLoanCalcs({
    loan,
  })

  const isOverCollateralised = ltv <= 100

  const { collateralAsset, collateralPrice, liquidationCollateralPrice } =
    useLoanCollateralCalcs({
      loan,
      collateralAddress,
      totalOutstanding,
      totalCollateralAmount,
    })

  return (
    <DashboardRow className={cns(s.root, className)}>
      <div className={s.column}>
        <InfoFieldValue
          label="Asset"
          value={
            borrowedAsset ? (
              <>
                {getPoolAssetIcon(borrowedAsset)} {borrowedAsset}
              </>
            ) : null
          }
        />
        <InfoFieldValue
          label="Interest rate"
          value={`${interestRate.toFixed(2)}%`}
          tooltip={interestRate.toFixed(4)}
        />
        <InfoFieldValue
          label="Loan Id"
          tooltip={loanId}
          tooltipMaxWidth={220}
          tooltipPosition="top-right"
          value={trimMiddleString(loanId, 4)}
        />
        <LoanStatusBadge loan={loan} />
      </div>

      <div className={s.column}>
        <div className={s.row}>
          <div className={s.subcolumn}>
            <InfoFieldValue
              label="Total debt"
              value={formatAsset(totalDebt, borrowedAsset)}
              sign={borrowedAsset}
              tooltip={totalDebt}
              secondaryValue={
                totalDebtUSD ? (
                  <>({formatAsset(totalDebtUSD, 'USD')} USD)</>
                ) : (
                  'Loading...'
                )
              }
              secondaryValueSize={16}
              secondaryValueTooltip={totalDebtUSD}
            />
            <InfoFieldValueCouple withLine>
              <InfoFieldValue
                label="Principal"
                value={formatAsset(principal, borrowedAsset)}
                sign={borrowedAsset}
                tooltip={principal}
              />
              <InfoFieldValue
                label="Interest"
                value={formatAsset(interest, borrowedAsset)}
                sign={borrowedAsset}
                tooltip={interest}
              />
            </InfoFieldValueCouple>
          </div>

          <div className={s.subcolumn}>
            <InfoFieldValue
              label="Issue date"
              value={
                <>
                  <FormattedDate format="DD–MMM–YYYY" date={issueDate} />
                  &nbsp;
                  <Text tag="span" color="secondary">
                    <FormattedDate format="hh:mm a" date={issueDate} />
                  </Text>
                </>
              }
              secondaryValue={<br />}
            />
            <InfoFieldValue
              label="Maturity date"
              valueColor={
                daysLeftToMaturity < 2 && !isCompleted ? 'error' : 'default'
              }
              value={
                <>
                  <FormattedDate format="DD–MMM–YYYY" date={maturityDate} />
                  &nbsp;
                  <Text
                    tag="span"
                    color={
                      daysLeftToMaturity < 2 && !isCompleted
                        ? 'errorSecondary'
                        : 'secondary'
                    }
                  >
                    <FormattedDate format="hh:mm a" date={maturityDate} />
                  </Text>
                </>
              }
            />
          </div>
        </div>

        {!isCompleted && isOverCollateralised && (
          <>
            <div className={s.horizontalDivider} />

            <div className={s.row}>
              <InfoFieldValueCouple>
                <InfoFieldValue
                  label={`Liquidation ${collateralAsset} price`}
                  value={
                    liquidationCollateralPrice
                      ? formatAsset(liquidationCollateralPrice, collateralAsset)
                      : 'LOADING...'
                  }
                  tooltip={liquidationCollateralPrice}
                  sign={liquidationCollateralPrice && 'USD'}
                />
                <InfoFieldValue
                  label={`Current ${collateralAsset} Price`}
                  value={
                    collateralPrice
                      ? formatAsset(collateralPrice, collateralAsset)
                      : 'LOADING...'
                  }
                  sign={collateralPrice && 'USD'}
                  tooltip={collateralPrice}
                />
              </InfoFieldValueCouple>
            </div>
          </>
        )}
      </div>

      <div className={s.column}>
        {isLoanActive(loan) && (
          <Button
            link={links.repayment(loanId)}
            className={s.action}
            fashion="greenapple-ghost"
            size={40}
          >
            REPAY
          </Button>
        )}
      </div>
    </DashboardRow>
  )
}
